/**
 * Unsent feedback text, kept per case in this browser (decision D7, 19 Oct 2026).
 *
 * A sales person opened "Give feedback" on a case, typed for two minutes and pressed
 * Cancel: the text was gone. The dialog now writes what is typed under
 * `i2q.feedback.draft.{caseId}.{surface}.{line}` while it is open, restores it on the next
 * opening on the same place (the same case, surface and line; `line` is `case` for feedback
 * on the whole case), and clears it when the feedback is saved or the person discards it.
 * One key per place, so the text of line 8 never comes back on line 12 or on the reply
 * draft (review of 19 Oct 2026).
 *
 * Storage can be missing or blocked: `lib/remembered.ts` keeps the value for this visit
 * then, so the dialog works the same way and only a reload loses the text. What comes back
 * is untrusted (another version of the app wrote it, or a person edited it): every field
 * is checked and capped, and anything unreadable reads as "no draft". Feedback without a
 * case keeps nothing.
 */

import {
  emptyFeedbackForm,
  FEEDBACK_FREQUENCIES,
  FEEDBACK_IMPACTS,
  FEEDBACK_KINDS,
  FEEDBACK_TEXT_MAX,
  FEEDBACK_TITLE_MAX,
  FEEDBACK_TOPICS,
  type FeedbackFormState,
} from "@/lib/api";
import { forget, readRemembered, remember } from "@/lib/remembered";

import { INTENT_OPTIONS } from "./labels";

/** Where the dialog was opened: the case, the surface and the line (null: the whole case). */
export interface FeedbackDraftPlace {
  caseId?: string | null;
  surface?: string | null;
  lineIndex?: number | null;
}

/** The browser key of the unsent text of one place, or null without a case. */
export function feedbackDraftKey(place: FeedbackDraftPlace | null | undefined): string | null {
  if (!place?.caseId) {
    return null;
  }
  const line = typeof place.lineIndex === "number" ? String(place.lineIndex) : "case";
  return `i2q.feedback.draft.${place.caseId}.${place.surface ?? "case"}.${line}`;
}

const RIGHT_KEYS = ["account", "item", "quantity", "unit_price", "currency"] as const;

/** True when the form holds text a person typed (a pick of a type or a tick is not text). */
export function hasTypedText(form: FeedbackFormState): boolean {
  const texts = [
    form.title,
    ...Object.values(form.fields),
    ...RIGHT_KEYS.map((key) => form.right[key] ?? ""),
  ];
  return texts.some((value) => typeof value === "string" && value.trim() !== "");
}

function isRecord(raw: unknown): raw is Record<string, unknown> {
  return raw != null && typeof raw === "object" && !Array.isArray(raw);
}

function oneOf<T extends string>(values: readonly T[], raw: unknown): T | null {
  return typeof raw === "string" && (values as readonly string[]).includes(raw) ? (raw as T) : null;
}

function capped(raw: unknown, max: number): string | null {
  return typeof raw === "string" ? raw.slice(0, max) : null;
}

/** A stored draft as a form, checked field by field; null when nothing usable is stored. */
export function toFeedbackDraft(raw: unknown): FeedbackFormState | null {
  if (!isRecord(raw)) {
    return null;
  }
  const kind = oneOf(FEEDBACK_KINDS, raw.kind);
  const form = emptyFeedbackForm(kind, oneOf(FEEDBACK_TOPICS, raw.topic));
  form.title = capped(raw.title, FEEDBACK_TITLE_MAX) ?? "";
  if (isRecord(raw.fields)) {
    for (const [key, value] of Object.entries(raw.fields)) {
      const text = capped(value, FEEDBACK_TEXT_MAX);
      if (text !== null && key.length <= 40) {
        form.fields[key] = text;
      }
    }
  }
  if (isRecord(raw.right)) {
    for (const key of RIGHT_KEYS) {
      const text = capped(raw.right[key], 60);
      if (text !== null) {
        form.right[key] = text;
      }
    }
    const intent = oneOf(
      INTENT_OPTIONS.map((option) => option.value),
      raw.right.intent,
    );
    if (intent) {
      form.right.intent = intent;
    }
  }
  form.impact = oneOf(FEEDBACK_IMPACTS, raw.impact);
  form.frequency = oneOf(FEEDBACK_FREQUENCIES, raw.frequency);
  form.includeMailText = raw.includeMailText === true;
  form.area = capped(raw.area, 20);
  return hasTypedText(form) ? form : null;
}

/** The unsent text of a place, or null (no case, nothing stored, unreadable). */
export function readFeedbackDraft(
  place: FeedbackDraftPlace | null | undefined,
): FeedbackFormState | null {
  const key = feedbackDraftKey(place);
  if (!key) {
    return null;
  }
  const raw = readRemembered(key, "");
  if (raw === "") {
    return null;
  }
  try {
    return toFeedbackDraft(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Keep the form for its place while it holds typed text; forget it once it holds none. */
export function keepFeedbackDraft(
  place: FeedbackDraftPlace | null | undefined,
  form: FeedbackFormState,
): void {
  const key = feedbackDraftKey(place);
  if (!key) {
    return;
  }
  if (hasTypedText(form)) {
    remember(key, JSON.stringify(form));
  } else {
    forget(key);
  }
}

/** Forget the unsent text of a place: after a save, or when the person discards it. */
export function clearFeedbackDraft(place: FeedbackDraftPlace | null | undefined): void {
  const key = feedbackDraftKey(place);
  if (key) {
    forget(key);
  }
}
